import type { LabelInValue } from 'models';
import { Alert, EntitySnippet, Form, FormActions, Item, Modal, message } from 'ui';
import { images } from 'common/utils';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { CommunitySelect, useCommunitySelectCreateOption } from 'features/communities/components/community-select';
import { OrganizationSelect } from 'features/organizations';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { UserSelect } from 'features/users/components/user-select';
import { useInviteProgramMutation } from '../../queries';

type P = {
  onClose: () => void;
  programId: string;
};

type ProgramInviteFormValues = {
  communities?: LabelInValue[];
  members?: LabelInValue[];
  organizations?: LabelInValue[];
};

const ProgramInviteForm = ({ onClose, programId }: P) => {
  const [form] = Form.useForm<ProgramInviteFormValues>();
  const createCommunityOption = useCommunitySelectCreateOption();

  const organizations = Form.useWatch('organizations', form);
  const communities = Form.useWatch('communities', form);

  const shouldShowBulkWarning = (organizations?.length ?? 0) > 0 || (communities?.length ?? 0) > 0;

  const { isPending, mutate: invite } = useInviteProgramMutation();

  const handleSubmit = (values: ProgramInviteFormValues) => {
    invite(
      {
        communityIds: values.communities?.map((community) => community.value) ?? [],
        organizationIds: values.organizations?.map((organization) => organization.value) ?? [],
        programId,
        userIds: values.members?.map((member) => member.value) ?? [],
      },
      {
        onSuccess() {
          message.success('Invitations have been sent.');
          onClose();
        },
      },
    );
  };

  return (
    <Form form={form} onFinish={handleSubmit}>
      <Item label="Invite Organizations" name="organizations">
        <OrganizationSelect<LabelInValue>
          createOption={(organization) => ({
            label: (
              <EntitySnippet
                avatarProps={{
                  fallbackPhoto: images.default.organizationProfilePhoto,
                  size: 'sm',
                  src: organization.squareLogo ?? organization.profilePhoto,
                }}
                titleProps={{ children: organization.name }}
              />
            ),
            value: organization.id,
          })}
          mode="multiple"
          query={{ filter: { scope: 'admin' } }}
        />
      </Item>

      <Item label="Invite Communities" name="communities">
        <CommunitySelect<LabelInValue>
          createOption={createCommunityOption}
          mode="multiple"
          query={{ filter: { scope: 'admin' } }}
        />
      </Item>

      <Item label="Enroll Members" name="members">
        <UserSelect<LabelInValue>
          createOption={(user) => ({ label: user.fullName, value: user.id })}
          mode="multiple"
          query={{ excludeProgramId: programId }}
        />
      </Item>

      {shouldShowBulkWarning ? (
        <Alert
          description="Inviting the entire communities or organizations can result in a large number of invitations being sent. Please review the selection carefully before submitting to avoid any errors"
          type="warning"
        />
      ) : null}

      <Modal.HugContent.Footer>
        <FormActions
          right={{ onClick: onClose }}
          submit={{ children: 'Submit', htmlType: 'submit', loading: isPending, type: 'primary' }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default ProgramInviteForm;
